import { Fragment, useMemo, useState, type ReactNode } from 'react';
import type { AllocDto, ColumnDto, ItemDto } from '../../shared/api';
import { INSTALL, QC, atMinimum, colList, fmtQty } from '../../shared/domain';
import { patch, refresh } from '../api';
import { Combo, PlaceSelect, day, errMsg, num, pl, useApp, when } from '../components/ui';
import { NewItemDialog } from '../components/dialogs';

type View = 'all' | 'warehouse' | 'used';
type SortKey = 'name' | 'place' | 'type' | 'qc' | 'project' | 'installation' | 'resource' | 'qty' | 'used';

/** One editable cell: shows the value; admins click it to edit in place. */
function Editable({ can, view, editor, title }: { can: boolean; view: ReactNode; editor: (done: () => void) => ReactNode; title?: string }) {
  const [on, setOn] = useState(false);
  if (!can) return <>{view}</>;
  if (on) return <div className="cell-edit">{editor(() => setOn(false))}</div>;
  return (
    <button className="cell-btn" title={title ?? 'Click to change'} onClick={() => setOn(true)}>
      {view}
    </button>
  );
}

const QcTag = ({ v }: { v: string }) => <span className={`tag dot ${v === 'Done' ? 'ok' : v === 'Pending' ? 'warn' : 'soft'}`}>{v}</span>;
const InstTag = ({ v }: { v: string }) => <span className={`tag dot ${v === 'Installed' ? 'ok' : v === 'Pending' ? 'warn' : 'soft'}`}>{v}</span>;
const dash = (v: unknown) => (v === '' || v == null ? <span className="faint">–</span> : String(v));

export function Inventory() {
  const { d, toast, openItem, openAdd, openTake, openReturn } = useApp();
  const [view, setView] = useState<View>('all');
  const [project, setProject] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ k: SortKey; dir: 1 | -1 }>({ k: 'name', dir: 1 });
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [adding, setAdding] = useState(false);
  const admin = d.isAdmin;
  const cols = d.b.columns;
  const o = d.b.options;

  const save = async (url: string, body: unknown, done: () => void) => {
    done();
    try {
      await patch(url, body);
      await refresh();
    } catch (e) {
      toast(errMsg(e));
    }
  };
  const saveItem = (i: ItemDto, body: unknown) => (done: () => void) => save(`/api/items/${i.id}`, body, done);
  const saveAlloc = (a: AllocDto, body: unknown) => (done: () => void) => save(`/api/allocations/${a.id}`, body, done);

  // ---- filtering ----
  const text = q.trim().toLowerCase();
  const hit = (i: ItemDto, a?: AllocDto) =>
    !text ||
    [i.name, i.type, i.resource, i.project, a?.project, d.where(a ? a.placeId : i.placeId, a ? a.shelfId : i.shelfId), ...Object.values(a ? a.extra : i.extra)].some((v) =>
      String(v ?? '').toLowerCase().includes(text),
    );
  const sortVal = (i: ItemDto, k: SortKey): string | number =>
    k === 'qty' ? i.qty : k === 'used' ? d.used(i) : k === 'place' ? d.where(i.placeId, i.shelfId).toLowerCase() : String(i[k] ?? '').toLowerCase();
  const sorted = useMemo(
    () => [...d.b.items].sort((x, y) => (sortVal(x, sort.k) < sortVal(y, sort.k) ? -1 : sortVal(x, sort.k) > sortVal(y, sort.k) ? 1 : 0) * sort.dir),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [d, sort],
  );
  const rows: { i: ItemDto; kids: AllocDto[] }[] = [];
  if (view === 'used') {
    for (const i of sorted)
      for (const a of i.allocations) if ((!project || a.project === project) && hit(i, a)) rows.push({ i, kids: [a] });
  } else {
    for (const i of sorted) {
      const kids = view === 'all' ? i.allocations.filter((a) => !project || a.project === project) : [];
      if (view === 'warehouse' && i.qty <= 0) continue;
      if (project && i.project !== project && !kids.length) continue;
      if (!hit(i) && !kids.some((a) => hit(i, a))) continue;
      rows.push({ i, kids });
    }
  }
  const totalUsed = d.allocs.filter((a) => !project || a.project === project).reduce((n, a) => n + a.qty, 0);

  const Th = ({ k, children, cls }: { k?: SortKey; children: ReactNode; cls?: string }) => (
    <th className={cls} aria-sort={k && sort.k === k ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
      {k ? (
        <button className="th-btn" onClick={() => setSort((s) => ({ k, dir: s.k === k ? (-s.dir as 1 | -1) : 1 }))}>
          {children}
          {sort.k === k ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
        </button>
      ) : (
        children
      )}
    </th>
  );

  // ---- cells shared by item rows and child rows ----
  const extraCell = (c: ColumnDto, value: string | number | undefined, onSave: (v: string | number | null) => (done: () => void) => void) => (
    <td key={c.id}>
      <Editable
        can={admin}
        view={dash(value)}
        editor={(done) =>
          c.kind === 'dropdown' ? (
            <Combo autoFocus value={String(value ?? '')} list={o[colList(c.id)] ?? []} onCommit={(v) => onSave(v || null)(done)} onCancel={done} />
          ) : (
            <input
              autoFocus
              defaultValue={String(value ?? '')}
              inputMode={c.kind === 'number' ? 'decimal' : undefined}
              onBlur={(e) => onSave(e.target.value.trim() || null)(done)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') done();
              }}
            />
          )
        }
      />
    </td>
  );
  const comboCell = (value: string, list: string[], onSave: (v: string) => (done: () => void) => void, allowEmpty = true) => (
    <Editable can={admin} view={dash(value)} editor={(done) => <Combo autoFocus value={value} list={list} allowEmpty={allowEmpty} onCommit={(v) => (v || allowEmpty ? onSave(v)(done) : done())} onCancel={done} />} />
  );
  const selectCell = (value: string, all: readonly string[], tag: ReactNode, onSave: (v: string) => (done: () => void) => void) => (
    <Editable
      can={admin}
      view={tag}
      editor={(done) => (
        <select autoFocus defaultValue={value} onChange={(e) => onSave(e.target.value)(done)} onBlur={done} onKeyDown={(e) => e.key === 'Escape' && done()}>
          {all.map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>
      )}
    />
  );
  const placeCell = (placeId: number | null, shelfId: number | null, onSave: (b: { placeId: number | null; shelfId: number | null }) => (done: () => void) => void) => (
    <Editable can={admin} view={dash(d.where(placeId, shelfId))} editor={(done) => <PlaceSelect autoFocus placeId={placeId} shelfId={shelfId} onBlur={done} onChange={(p, s) => onSave({ placeId: p, shelfId: s })(done)} />} />
  );

  const itemRow = (i: ItemDto, kids: AllocDto[]) => {
    const used = d.used(i);
    const low = atMinimum(i.qty, i.minQty);
    const open = !collapsed.has(i.id);
    const hasKids = view === 'all' && kids.length > 0;
    return (
      <tr key={`i${i.id}`} className={`irow${low ? ' low' : ''}`}>
        <td className="name">
          <div className="namecell">
          {hasKids ? (
            <button className="twist" aria-label={open ? 'Hide projects' : 'Show projects'} onClick={() => setCollapsed((s) => (s.has(i.id) ? (s.delete(i.id), new Set(s)) : new Set(s.add(i.id))))}>
              {open ? '▾' : '▸'}
            </button>
          ) : (
            <span className="twist" />
          )}
          <Editable
            can={admin}
            title="Click to rename"
            view={<b>{i.name}</b>}
            editor={(done) => <Combo autoFocus value={i.name} list={d.names} allowEmpty={false} onCommit={(v) => (v.trim() ? saveItem(i, { name: v })(done) : done())} onCancel={done} />}
          />
          <button className="linkbtn open" onClick={() => openItem(i.id)} title="Open details and history">
            Details
          </button>
          </div>
        </td>
        <td>{placeCell(i.placeId, i.shelfId, (b) => saveItem(i, b))}</td>
        <td>{comboCell(i.type, o.type ?? [], (v) => saveItem(i, { type: v }))}</td>
        <td>{selectCell(i.qc, QC, <QcTag v={i.qc} />, (v) => saveItem(i, { qc: v }))}</td>
        <td>{comboCell(i.project, o.project ?? [], (v) => saveItem(i, { project: v }))}</td>
        <td>{selectCell(i.installation, INSTALL, <InstTag v={i.installation} />, (v) => saveItem(i, { installation: v }))}</td>
        <td>{comboCell(i.resource, o.resource ?? [], (v) => saveItem(i, { resource: v }))}</td>
        <td className={`num${low ? ' lowq' : ''}`}>
          <Editable
            can={admin}
            title="Click to correct the count (use + Add / − Take for real movements)"
            view={
              <span className={low ? 'tag bad' : ''} title={i.minQty != null ? `Minimum ${fmtQty(i.minQty)}` : undefined}>
                {fmtQty(i.qty)}
              </span>
            }
            editor={(done) => (
              <input
                autoFocus
                className="qty-in"
                defaultValue={fmtQty(i.qty)}
                inputMode="decimal"
                onBlur={(e) => {
                  const n = num(e.target.value);
                  if (Number.isFinite(n) && n >= 0 && n !== i.qty) void saveItem(i, { qty: n })(done);
                  else done();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  if (e.key === 'Escape') done();
                }}
              />
            )}
          />
        </td>
        <td className="num">
          {used > 0 ? (
            <span className="used" tabIndex={0}>
              {fmtQty(used)}
              <span className="pop" role="tooltip">
                {i.allocations.map((a) => (
                  <span key={a.id}>
                    {a.project}: <b>{fmtQty(a.qty)}</b> ({a.installation.toLowerCase()})
                  </span>
                ))}
              </span>
            </span>
          ) : (
            <span className="faint">0</span>
          )}
        </td>
        {cols.map((c) => extraCell(c, i.extra[c.id], (v) => saveItem(i, { extra: { [c.id]: v } })))}
        <td className="last">{i.last ? <span title={`${when(i.last.at)} · ${i.last.user}: ${i.last.text}`}>{`${i.last.user} · ${day(i.last.at)} · ${i.last.text}`}</span> : <span className="faint">–</span>}</td>
        {admin && (
          <td className="acts nowrap">
            <button className="btn small" onClick={() => openAdd(i.id)} title="Units came in (e.g. bought)">
              + Add
            </button>
            <button className="btn small" disabled={i.qty <= 0} onClick={() => openTake(i.id)} title="Assign to a project or take out">
              − Take
            </button>
          </td>
        )}
      </tr>
    );
  };

  const kidRow = (i: ItemDto, a: AllocDto, flat: boolean) => (
    <tr key={`a${a.id}`} className="krow">
      <td className="name">
        <div className="namecell">
        <span className="twist" />
        {flat ? (
          <>
            <button className="linkbtn" onClick={() => openItem(i.id)}>
              <b>{i.name}</b>
            </button>
          </>
        ) : (
          <span className="kid">
            ↳ {a.project} · <b>{fmtQty(a.qty)}</b>
          </span>
        )}
        </div>
      </td>
      <td>{placeCell(a.placeId, a.shelfId, (b) => saveAlloc(a, b))}</td>
      <td className="faint">{i.type || '–'}</td>
      <td>{selectCell(a.qc, QC, <QcTag v={a.qc} />, (v) => saveAlloc(a, { qc: v }))}</td>
      <td>{comboCell(a.project, o.project ?? [], (v) => saveAlloc(a, { project: v }), false)}</td>
      <td>{selectCell(a.installation, INSTALL, <InstTag v={a.installation} />, (v) => saveAlloc(a, { installation: v }))}</td>
      <td>{comboCell(a.resource, o.resource ?? [], (v) => saveAlloc(a, { resource: v }))}</td>
      <td className="num faint">–</td>
      <td className="num">
        <b>{fmtQty(a.qty)}</b>
      </td>
      {cols.map((c) => extraCell(c, a.extra[c.id], (v) => saveAlloc(a, { extra: { [c.id]: v } })))}
      <td className="last">
        <span title={a.note}>{`${a.createdBy} · ${day(a.createdAt)}${a.note ? ` · ${a.note}` : ''}`}</span>
      </td>
      {admin && (
        <td className="acts">
          <button className="btn small" onClick={() => openReturn(a.id)} title="Bring units back to the warehouse">
            Return
          </button>
        </td>
      )}
    </tr>
  );

  return (
    <section>
      <div className="toolbar">
        <div className="seg" role="radiogroup" aria-label="Show">
          {(
            [
              ['all', 'All'],
              ['warehouse', 'In warehouse'],
              ['used', 'Used'],
            ] as [View, string][]
          ).map(([v, l]) => (
            <button key={v} className={`btn ${view === v ? 'on' : ''}`} role="radio" aria-checked={view === v} onClick={() => setView(v)}>
              {l}
            </button>
          ))}
        </div>
        <select value={project} onChange={(e) => setProject(e.target.value)} aria-label="Project">
          <option value="">All projects</option>
          {(o.project ?? []).map((p) => (
            <option key={p}>{p}</option>
          ))}
        </select>
        <input className="search" type="search" placeholder="Search name, type, place …" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="grow" />
        {admin && (
          <button className="btn primary" onClick={() => setAdding(true)}>
            + New item
          </button>
        )}
      </div>
      <p className="meta summary">
        {view === 'used'
          ? `${pl(rows.length, 'assignment')}, ${fmtQty(totalUsed)} units used${project ? ` for ${project}` : ''}.`
          : `${pl(rows.length, 'item')}${project ? ` for ${project}` : ''}. QTY = in the warehouse now; Used = assigned to projects${view === 'all' ? ' (shown under each item)' : ''}.`}
      </p>
      <div className="sheet">
        <table className="grid">
          <thead>
            <tr>
              <Th k="name" cls="name">
                Name
              </Th>
              <Th k="place">Place</Th>
              <Th k="type">Type</Th>
              <Th k="qc">QC</Th>
              <Th k="project">Project</Th>
              <Th k="installation">Installation</Th>
              <Th k="resource">Resource</Th>
              <Th k="qty" cls="num">
                QTY
              </Th>
              <Th k="used" cls="num">
                Used
              </Th>
              {cols.map((c) => (
                <th key={c.id}>{c.label}</th>
              ))}
              <th>Last activity</th>
              {admin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ i, kids }) =>
              view === 'used' ? (
                kidRow(i, kids[0]!, true)
              ) : (
                <Fragment key={i.id}>
                  {itemRow(i, kids)}
                  {view === 'all' && !collapsed.has(i.id) && kids.map((a) => kidRow(i, a, false))}
                </Fragment>
              ),
            )}
            {!rows.length && (
              <tr>
                <td colSpan={12 + cols.length} className="empty">
                  {d.b.items.length ? 'Nothing matches.' : admin ? 'No items yet. Add the first one with “+ New item”.' : 'No items yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {adding && <NewItemDialog onClose={() => setAdding(false)} />}
    </section>
  );
}
